import { useAskQwick } from "domains/aiChat/hooks/useAskQwick";
import { useAuth, useIsAdmin, useUserRoleForLocation } from "domains/auth";
import { jobsList } from "domains/hire/layouts/HireLayout/constants";
import { useSetAtom } from "jotai";
import {
  adminInvoicing,
  adminItems as adminItemsDifferent,
  adminPermissions,
  adminTasks,
  agentChat,
  agentChatOnboarding,
  getMessages,
  locations,
  logout,
  marketplaceConnect,
  marketplaceDashboards,
  marketplaceHistory,
  marketplaceHome,
  marketplacePay,
  marketplacePostGig,
  marketplacePostGigFromNav,
  marketplaceSchedule,
  needHelp,
  powerUser,
  settings,
} from "layouts/BaseLayout/constants";
import type { NavItem, NavItemLink } from "layouts/BaseLayout/types";
import { useUnreadMessageCount } from "layouts/BaseLayout/useUnreadMessageCount";
import type { SidebarGroup } from "layouts/SidebarNavigation";
import { MANAGE_PERMISSIONS, useHasCapability } from "layouts/SuperAdminPanel/capabilities";
import { useFeatureFlag } from "providers/LaunchDarkly";
import { agentChatSidebarAtom } from "services/atoms";
import { useActiveLocationId } from "services/locationOrgContext";
import { powerUserMenuAtom } from "services/powerUser";
import { useShowJobsMenuItem } from "./useShowHireSwitch";

export const useShiftsNavItems = (): {
  main: Array<NavItem>;
  secondary: Array<NavItem>;
  drawer: Array<NavItem>;
  topBar: Array<NavItem>;
  bottomBar: Array<NavItemLink>;
  admin: Array<NavItem>;
  inbox: NavItemLink;
  agentChat: NavItem | null;
  sidebar: Array<SidebarGroup>;
} => {
  const { logout: doLogout } = useAuth();

  const [activeLocationId] = useActiveLocationId();
  const unreadMessageCount = useUnreadMessageCount(activeLocationId);
  const inbox = getMessages(unreadMessageCount);

  const canUploadTaskTime = useFeatureFlag("task-upload-allowed");
  const canManagePermissions = useHasCapability(MANAGE_PERMISSIONS);
  const adminItems: Array<NavItem> = useIsAdmin()
    ? [
        ...adminItemsDifferent,
        ...(canUploadTaskTime ? [adminTasks] : []),
        adminInvoicing,
        ...(canManagePermissions ? [adminPermissions] : []),
      ]
    : [];

  const isOnsiteManager = useUserRoleForLocation(activeLocationId ?? -1) === "ONSITE_MANAGER";
  const postGig = useFeatureFlag("agent-web-posting-opt-in") ? marketplacePostGigFromNav : marketplacePostGig;

  const agentChatEnabled = useFeatureFlag("agent-web-header-button-admin");
  const setAgentChatSidebarOpen = useSetAtom(agentChatSidebarAtom);
  const toggleChat = () => setAgentChatSidebarOpen((prev) => !prev);
  const askQwick = useAskQwick(toggleChat);
  const chatItem = askQwick.restyled ? agentChatOnboarding(askQwick.onClick) : agentChat(toggleChat);
  const agentChatItem = agentChatEnabled ? chatItem : null;
  const agentChatButton = agentChatItem ? [agentChatItem] : [];

  const powerToolsFlag = useFeatureFlag("power-tools");
  const setPowerUserMenuOpen = useSetAtom(powerUserMenuAtom);
  const togglePowerUser = () => setPowerUserMenuOpen((prev) => !prev);
  const powerUserItem = powerToolsFlag.includes("all") ? powerUser(togglePowerUser) : null;

  const jobsItem = useShowJobsMenuItem() ? [jobsList] : [];

  const commonItems = { inbox, admin: adminItems, agentChat: agentChatItem };

  if (isOnsiteManager) {
    return {
      main: [marketplaceSchedule],
      secondary: [settings],
      drawer: [],
      bottomBar: [],
      topBar: [inbox, ...agentChatButton],
      sidebar: [[marketplaceSchedule], ["admin"], [settings]],
      ...commonItems,
    };
  }
  return {
    main: [
      postGig,
      ...jobsItem,
      marketplaceHome,
      marketplacePay,
      marketplaceSchedule,
      marketplaceConnect,
      marketplaceDashboards,
    ],
    secondary: [
      locations,
      marketplaceHistory,
      needHelp,
      ...(powerUserItem ? [powerUserItem] : []),
      settings,
      logout(doLogout),
    ],
    drawer: [
      ...jobsItem,
      marketplacePay,
      marketplaceHistory,
      locations,
      needHelp,
      ...(powerUserItem ? [powerUserItem] : []),
      settings,
      ...adminItems,
    ],
    topBar: [needHelp, inbox, ...agentChatButton],
    bottomBar: [marketplaceHome, marketplaceSchedule, postGig, marketplaceConnect],
    sidebar: [
      [
        postGig,
        ...jobsItem,
        marketplaceHome,
        marketplaceSchedule,
        marketplacePay,
        marketplaceDashboards,
        marketplaceConnect,
      ],
      [locations, marketplaceHistory],
      [...(powerUserItem ? [powerUserItem] : []), "admin"],
      [needHelp, settings, logout(doLogout)],
    ],
    ...commonItems,
  };
};
